import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import menuitems from '../menuitems.js';
import RoleService from '../Services/roleService';
import { navbutton_click, submenubutton_click, palette_set_open } from '../redux/actions';
import { iconsEnabled } from '../Helpers/iconHelper';

// Command-palette style menu search. Walks the role-filtered menu tree and lists
// every page whose own label (or its section's label) matches the query, as a flat
// clickable list. Ported from the CD-5354 sidebar search into the Bravo icon-rail model.
export default function NavSearchFlyout({ onClose, onNavigate }) {
  const dispatch = useDispatch();
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  // Focus the input as soon as the flyout opens so the user can type immediately.
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const q = query.trim().toLowerCase();

  // Case-insensitive "contains" over a menu entry's visible label (text / linktext).
  const matches = (item) =>
    (item.text || '').toLowerCase().includes(q) || (item.linktext || '').toLowerCase().includes(q);

  // Build a flat, role-filtered result list: { section > page }. A section whose own
  // label matches contributes all its pages (mirrors CD-5354's parent-match rule); a
  // top-level page with no submenu is its own result.
  const results = [];
  if (q !== '') {
    for (let s = 0; s < menuitems.length; s++) {
      const section = menuitems[s];
      if (section.visible === false || section.shown === false) continue;
      if (!RoleService.CheckUserPermission('menu', section.key)) continue;

      const sectionMatched = matches(section);
      const submenu = section.submenu || [];

      if (submenu.length === 0) {
        if (sectionMatched) {
          results.push({ section, sectionIndex: s, sub: section, subIndex: 0, hasSub: false });
        }
        continue;
      }

      for (let i = 0; i < submenu.length; i++) {
        const sub = submenu[i];
        if (!RoleService.CheckUserPermission('submenu', sub.key)) continue;
        if (sectionMatched || matches(sub)) {
          results.push({ section, sectionIndex: s, sub, subIndex: i, hasSub: true });
        }
      }
    }
  }

  // Navigate exactly like the rail/flyout do: set the section, then (for a real
  // sub-item) the active sub + content link. A no-submenu page only sets the section.
  const go = (r) => {
    dispatch(
      navbutton_click(r.sectionIndex, r.section.link, r.section.text, r.section.linktext, r.section.icon, r.section.url)
    );
    if (r.hasSub) {
      const sub = r.sub;
      dispatch(
        submenubutton_click(
          r.subIndex,
          sub.link,
          sub.linktext ? sub.linktext : sub.text,
          sub.icon,
          sub.url,
          r.sectionIndex
        )
      );
    }
    (onNavigate || onClose)();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
  };

  // Open the record-search palette (CD-5425), closing this flyout as it goes: never stack two
  // overlays with two backdrops. `seed` carries a forwarded first keystroke.
  const openRecordSearch = (seed = '') => {
    onClose();
    dispatch(palette_set_open(true, seed));
  };

  // Typing into the decoy must do something. Without this, the predictable failure is someone
  // typing at what looks like a field and seeing nothing happen. Modifier combos are excluded so
  // Ctrl-K and friends still reach their own handlers.
  const onDecoyKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openRecordSearch();
      return;
    }

    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      openRecordSearch(e.key);
    }
  };

  return (
    <>
      <div
        className='flyout-catch'
        onClick={onClose}
      />
      <aside
        className='flyout'
        onKeyDown={onKeyDown}
      >
        <div className='flyout-head'>
          <div className='fh-txt'>
            <div className='eyebrow'>Search</div>
            <h3>Find a page</h3>
          </div>
          <div className='sp' />
          <button
            type='button'
            className='fclose'
            onClick={onClose}
            aria-label='Close'
          >
            <i className='fa-solid fa-xmark' />
          </button>
        </div>

        <div className='flyout-search'>
          <i className='fa-solid fa-magnifying-glass' />
          <input
            ref={inputRef}
            type='text'
            placeholder='Search menu...'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {/* Entry point into record search. A <button>, never a readonly <input>: a text field that
            opens a modal is an accessibility trap - screen readers announce an editable field,
            keyboard users tab in and type, and nothing happens. Same precedent as .flyout-item,
            which is a button styled as a list row. */}
        <div className='flyout-searchlike-wrap'>
          {/* "Records", not "Search" - the panel head already says SEARCH, and repeating it here
              reads as a duplicate label rather than a second kind of search. */}
          <div className='eyebrow'>Records</div>
          <button
            type='button'
            className='flyout-searchlike'
            aria-label='Search records'
            onClick={() => openRecordSearch()}
            onKeyDown={onDecoyKeyDown}
          >
            <i className='fa-solid fa-magnifying-glass' />
            <span className='fsl-text'>Find a record&hellip;</span>
            {/* Teaches the keyboard path so mouse-first users graduate to the shortcut. */}
            <kbd className='fsl-kbd'>Ctrl K</kbd>
          </button>
        </div>

        <div className='flyout-list'>
          {q === '' ? (
            <div className='flyout-hint'>Type to search the menu&hellip;</div>
          ) : results.length === 0 ? (
            <div className='flyout-hint'>No pages match &ldquo;{query}&rdquo;.</div>
          ) : (
            results.map((r, idx) => {
              const label = r.sub.linktext ? r.sub.linktext : r.sub.text;
              return (
                <button
                  type='button'
                  key={`search-res-${r.sectionIndex}-${r.subIndex}-${idx}`}
                  className='flyout-item'
                  onClick={() => go(r)}
                >
                  {iconsEnabled() && <i className={r.sub.icon || r.section.icon} />}
                  <span className='sr-txt'>
                    {r.hasSub ? <span className='sr-section'>{r.section.text}</span> : null}
                    <span className='sr-page'>{label}</span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      </aside>
    </>
  );
}
